import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { PaymentTracker } from '../payments/PaymentTracker';
import { Mission } from '../../types';
import { useStore, getUserById } from '../../store';
import { sanitize } from '../../lib/security';
import { t } from '../../lib/i18n';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface MissionDetailModalProps {
  mission: Mission | null;
  open: boolean;
  onClose: () => void;
  onOpenAuth: () => void;
  onReport: (userId: string, name: string) => void;
}

export const MissionDetailModal: React.FC<MissionDetailModalProps> = ({
  mission, open, onClose, onOpenAuth, onReport,
}) => {
  const { currentUser, applyMission, startMission, completeMission, setActiveChatId, lang } = useStore();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  if (!mission) return null;
  const poster = getUserById(mission.posterId);
  const hiredUser = mission.hiredId ? getUserById(mission.hiredId) : null;
  const isOwner = currentUser?.id === mission.posterId;
  const isHired = currentUser?.id === mission.hiredId;
  const alreadyApplied = currentUser ? mission.applicantIds.includes(currentUser.id) : false;
  const platformFee = (mission.reward * 0.07).toFixed(2);
  const totalCharged = (mission.reward * 1.07).toFixed(2);

  const handleApply = async () => {
    if (!currentUser) { onClose(); onOpenAuth(); return; }
    setLoading(true);
    const { error, chatId } = applyMission(mission.id);
    setLoading(false);
    if (error) { toast.error(t(lang, error)); return; }
    toast.success(t(lang, 'toast_applied'));
    if (chatId) { setActiveChatId(chatId); navigate('/chats'); }
    onClose();
  };

  const handleStart = () => {
    const err = startMission(mission.id);
    if (err) { toast.error(t(lang, err)); return; }
    toast.success(t(lang, 'toast_started'));
    onClose();
  };

  const handleComplete = () => {
    const err = completeMission(mission.id);
    if (err) { toast.error(t(lang, err)); return; }
    toast.success(t(lang, 'toast_done'));
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title={mission.title} maxWidth="640px">
      <div className="mission-detail">
        <div className="poster-deco">
          <span className="star-deco">★</span> WANTED <span className="star-deco">★</span>
        </div>

        {/* Reward + meta */}
        <div className="mission-detail-top">
          <div>
            <span className="reward-amount">€{mission.reward}</span>
            <div className="mission-fee-note">
              Platform fee: €{platformFee} · Total: €{totalCharged}
            </div>
          </div>
          <span className={`status-badge ${
            mission.status === 'open' ? 'status-open' :
            mission.status === 'active' ? 'status-active' :
            mission.status === 'started' ? 'status-started' :
            mission.status === 'done' ? 'status-done' : 'status-cancelled'
          }`}>{mission.status.toUpperCase()}</span>
        </div>

        <div className="mission-meta-row">
          <span>⏱ {mission.estimatedHours}h</span>
          <span>📍 {sanitize(mission.location || 'Remote')}</span>
          <span>📂 {sanitize(mission.category)}</span>
          {hiredUser && <span>🤠 Hired: {sanitize(hiredUser.firstName)} {sanitize(hiredUser.lastName)}</span>}
        </div>

        <hr className="divider" />
        <p className="mission-description">{sanitize(mission.description)}</p>

        {mission.skills.length > 0 && (
          <div className="mission-skills-section">
            <span className="form-label">{t(lang, 'mission_skills')}</span>
            <div className="bounty-skills">
              {mission.skills.map(s => <span key={s} className="skill-tag">{sanitize(s)}</span>)}
            </div>
          </div>
        )}

        {poster && (
          <div className="mission-poster-row">
            <span>{t(lang, 'mission_posted_by')}: <strong>{sanitize(poster.firstName)} {sanitize(poster.lastName)}</strong></span>
            {!isOwner && (
              <button className="report-btn" onClick={() => onReport(poster.id, `${poster.firstName} ${poster.lastName}`)}>
                ⚠ {t(lang, 'report_btn')}
              </button>
            )}
          </div>
        )}

        {/* Payment tracker */}
        {mission.status !== 'open' && (
          <PaymentTracker mission={mission} label={{
            title: t(lang, 'mission_payment_progress'),
            hire: t(lang, 'mission_pay_hire'),
            start: t(lang, 'mission_pay_start'),
            finish: t(lang, 'mission_pay_finish'),
            paid: 'Paid', remaining: 'Remaining',
          }} />
        )}

        {/* Actions */}
        <div className="mission-detail-actions">
          {!currentUser && (
            <button className="btn btn-gold" onClick={() => { onClose(); onOpenAuth(); }}>
              Sign in to Apply
            </button>
          )}
          {currentUser && !isOwner && !isHired && mission.status === 'open' && (
            alreadyApplied
              ? <span className="applied-note">✓ {t(lang, 'mission_applied')}</span>
              : <button className="btn btn-gold" onClick={handleApply} disabled={loading}>
                  🏹 {t(lang, 'mission_apply')}
                </button>
          )}
          {isOwner && (mission.status === 'active') && (
            <button className="btn btn-outline" onClick={handleStart}>
              ▶ {t(lang, 'mission_start')}
            </button>
          )}
          {isOwner && mission.status === 'started' && (
            <button className="btn btn-success" onClick={handleComplete}>
              ✓ {t(lang, 'mission_done')}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};
